import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TerminosPage() {
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="terminos-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
            data-testid="terminos-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <Link to="/" className="text-lg font-bold tracking-tight text-zinc-900">
            Dealider
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 leading-relaxed text-[15px]">
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-2">
          Términos y Condiciones de Uso
        </h1>
        <p className="text-zinc-600 mb-1">Plataforma Dealider · app.dealider.com</p>
        <p className="text-sm text-zinc-500 mb-10">Última actualización: 21 de mayo de 2026</p>

        <Section n="1" title="Aceptación de los Términos y Condiciones">
          <p>
            El acceso, registro o uso de la plataforma Dealider (la “Plataforma”) implica la
            aceptación plena y sin reservas de los presentes Términos y Condiciones de Uso (los
            “Términos”). Si el Usuario no está de acuerdo con cualquier disposición, deberá
            abstenerse de utilizar la Plataforma. Estos Términos constituyen un contrato vinculante
            entre el Usuario y Dragon Technologies SAPI de CV.
          </p>
        </Section>

        <Section n="2" title="Definiciones">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Plataforma:</strong> el sistema CRM Dealider accesible en app.dealider.com y
              sus aplicaciones móviles asociadas.
            </li>
            <li>
              <strong>Usuario:</strong> persona física o moral que se registra o utiliza la
              Plataforma, incluyendo administradores, vendedores y colaboradores.
            </li>
            <li>
              <strong>Cuenta:</strong> credenciales y configuración que permiten al Usuario acceder
              a la Plataforma.
            </li>
            <li>
              <strong>Datos de Clientes:</strong> información de prospectos, clientes o terceros
              que el Usuario carga o genera dentro de la Plataforma.
            </li>
            <li>
              <strong>Servicios:</strong> funcionalidades, módulos, integraciones y soporte que
              ofrece la Plataforma bajo los planes contratados.
            </li>
          </ul>
        </Section>

        <Section n="3" title="Descripción del Servicio">
          <p>
            Dealider es una plataforma SaaS de gestión de relaciones con clientes (CRM)
            orientada a equipos de ventas, marketing y atención. Permite operar pipelines,
            comunicaciones por WhatsApp, llamadas, campañas masivas, automatizaciones con
            inteligencia artificial e integraciones con proveedores externos. El alcance funcional
            disponible depende del plan contratado y puede actualizarse periódicamente.
          </p>
        </Section>

        <Section n="4" title="Registro, Cuentas de Usuario y Seguridad">
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">4.1 Registro</h3>
          <p>
            Para utilizar la Plataforma, el Usuario deberá registrar una Cuenta proporcionando
            información veraz, completa y actualizada. Dealider podrá rechazar o suspender
            registros que considere fraudulentos, duplicados o contrarios a estos Términos.
          </p>
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">
            4.2 Seguridad de Credenciales
          </h3>
          <p>
            El Usuario es responsable de la confidencialidad de sus credenciales y de toda
            actividad realizada bajo su Cuenta. Deberá notificar de inmediato cualquier uso no
            autorizado al correo soporte@dealider.com. Se recomienda habilitar autenticación de
            dos factores cuando esté disponible.
          </p>
        </Section>

        <Section n="5" title="Planes de Suscripción, Pagos y Facturación">
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">5.1 Planes</h3>
          <p>
            La Plataforma se ofrece bajo planes de suscripción mensuales o anuales. Cada plan
            define los módulos habilitados, límites de uso, cantidad de usuarios y servicios
            incluidos.
          </p>
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">5.2 Pagos</h3>
          <p>
            Los pagos se realizan por adelantado mediante los métodos disponibles. La falta de
            pago en la fecha pactada podrá generar la suspensión inmediata del servicio.
          </p>
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">5.3 Facturación</h3>
          <p>
            Dealider emitirá CFDI conforme a la legislación fiscal mexicana cuando el Usuario
            proporcione sus datos fiscales completos dentro del plazo establecido.
          </p>
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">
            5.4 Cancelación y No Reembolso
          </h3>
          <p>
            Salvo disposición legal en contrario, los importes pagados por períodos en curso no
            son reembolsables. El Usuario puede cancelar su suscripción en cualquier momento; la
            cancelación surte efectos al final del ciclo facturado.
          </p>
        </Section>

        <Section n="6" title="Obligaciones y Uso Aceptable del Usuario">
          <p>Queda prohibido utilizar la Plataforma para:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Realizar actividades ilícitas, fraudulentas o engañosas.</li>
            <li>Enviar spam, mensajes no solicitados o violar la normativa de WhatsApp o Meta.</li>
            <li>Cargar datos personales sin la base legal correspondiente.</li>
            <li>Vulnerar derechos de terceros, incluidos derechos de propiedad intelectual.</li>
            <li>
              Intentar acceder a sistemas, datos o cuentas ajenas, o realizar ingeniería inversa
              sobre la Plataforma.
            </li>
            <li>Interferir con la disponibilidad, integridad o seguridad de los Servicios.</li>
          </ul>
        </Section>

        <Section n="7" title="Datos de Clientes y Contenido del Usuario">
          <p>
            El Usuario conserva la titularidad de los Datos de Clientes que carga en la
            Plataforma. Dealider actúa como Encargado del tratamiento únicamente en lo necesario
            para prestar los Servicios y conforme a la legislación aplicable. El Usuario es el
            responsable del tratamiento frente a los titulares de dichos datos.
          </p>
        </Section>

        <Section n="8" title="Propiedad Intelectual">
          <p>
            Todos los derechos de propiedad intelectual sobre la Plataforma, su software, marcas,
            diseño, documentación y contenido son propiedad de Dragon Technologies SAPI de CV o
            de sus licenciantes. El acceso a la Plataforma no otorga al Usuario derecho alguno
            distinto a la licencia limitada, no exclusiva e intransferible para utilizarla bajo
            estos Términos.
          </p>
        </Section>

        <Section n="9" title="Confidencialidad">
          <p>
            Cada parte se obliga a mantener la confidencialidad de la información no pública que
            reciba de la otra en el marco de los Servicios, utilizándola únicamente para los
            fines del contrato y aplicando medidas de protección razonables.
          </p>
        </Section>

        <Section n="10" title="Garantías y Exclusión de Garantías">
          <p>
            La Plataforma se ofrece “tal cual” y “según disponibilidad”. Dealider realiza
            esfuerzos razonables para mantener su correcto funcionamiento, pero no garantiza que
            sea ininterrumpida, libre de errores o totalmente segura. En la máxima medida
            permitida por la ley, se excluyen las garantías implícitas de comerciabilidad e
            idoneidad para un propósito particular.
          </p>
        </Section>

        <Section n="11" title="Limitación de Responsabilidad">
          <p>
            En la máxima medida permitida por la ley, la responsabilidad total de Dealider frente
            al Usuario por cualquier reclamación derivada de los Servicios no excederá los
            importes efectivamente pagados por el Usuario en los doce meses anteriores al hecho
            que originó la reclamación. Dealider no será responsable por daños indirectos,
            incidentales, especiales, consecuenciales o lucro cesante.
          </p>
        </Section>

        <Section n="12" title="Indemnización">
          <p>
            El Usuario se obliga a mantener indemne y a sacar en paz y a salvo a Dealider, sus
            socios, directivos y empleados frente a cualquier reclamación de terceros derivada
            del uso indebido de la Plataforma, del incumplimiento de estos Términos o de la
            violación de derechos de terceros.
          </p>
        </Section>

        <Section n="13" title="Terminación y Suspensión">
          <p>
            Dealider podrá suspender o terminar la Cuenta del Usuario en caso de incumplimiento
            grave de los Términos, falta de pago, fraude o requerimiento de autoridad competente.
            El Usuario podrá descargar sus datos durante el período de gracia establecido en el
            Aviso de Privacidad.
          </p>
        </Section>

        <Section n="14" title="Modificaciones a los Términos">
          <p>
            Dealider podrá modificar estos Términos en cualquier momento. Las modificaciones se
            notificarán a través de la Plataforma o por correo electrónico, y entrarán en vigor
            en la fecha indicada. El uso continuado de la Plataforma con posterioridad a dicha
            fecha constituye aceptación de los Términos modificados.
          </p>
        </Section>

        <Section n="15" title="Ley Aplicable y Resolución de Controversias">
          <p>
            Estos Términos se rigen por las leyes federales de los Estados Unidos Mexicanos. Para
            la interpretación y cumplimiento de los mismos, las partes se someten a la
            jurisdicción de los tribunales competentes de la Ciudad de México, renunciando a
            cualquier otro fuero que pudiera corresponderles.
          </p>
        </Section>

        <Section n="16" title="Disposiciones Generales">
          <p>
            Si alguna disposición de estos Términos fuera declarada inválida o inejecutable, las
            demás continuarán en plena vigencia. La falta de ejercicio por parte de Dealider de
            cualquier derecho no constituirá renuncia al mismo. Estos Términos, junto con el
            Aviso de Privacidad, constituyen el acuerdo completo entre las partes respecto del
            objeto de los mismos.
          </p>
        </Section>

        <Section n="17" title="Contacto">
          <p>
            Para cualquier consulta relativa a estos Términos, el Usuario puede comunicarse a:
          </p>
          <p className="mt-2">
            <strong>Email:</strong> soporte@dealider.com
            <br />
            <strong>Web:</strong>{" "}
            <a
              href="https://app.dealider.com"
              className="text-indigo-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              https://app.dealider.com
            </a>
          </p>
        </Section>

        <footer className="mt-16 pt-6 border-t border-zinc-200 text-xs text-zinc-500 text-center">
          Última actualización: 21 de mayo de 2026 · Dealider
        </footer>
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 mb-3">
        {n}. {title}
      </h2>
      <div className="text-zinc-700 space-y-3">{children}</div>
    </section>
  );
}
